import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { GameRoomStatus, Room } from '@/redux/types/room'
import { useAppDispatch } from '@/redux/hooks'
import { getRoomDetails, updateRoomDetails } from '@/redux/asyncThunks/room'
import { toast } from 'sonner'
import {
  startNextRoundThunk,
  fetchTimerConfigurationThunk,
} from '@/redux/asyncThunks/timerConfiguration'
import { useState, useCallback, useMemo } from 'react'
import { TimerMode } from '@/components/settings/timer-configuration/types'

export default function StartGameButton({
  currentRoom,
  disabled,
  className,
}: {
  currentRoom: Room
  disabled?: boolean
  className?: string
}) {
  const isNotReady = !currentRoom?.is_ready_to_start
  const dispatch = useAppDispatch()
  const { roomid, scenario_title } = currentRoom
  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const [isStarting, setIsStarting] = useState(false)

  const handleStartGame = useCallback(async () => {
    if (
      isNotReady ||
      currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
      isGameCompleted ||
      disabled ||
      isStarting
    ) {
      return
    }

    try {
      setIsStarting(true)

      await dispatch(
        updateRoomDetails({
          roomId: roomid,
          scenario_title: scenario_title!,
          status: GameRoomStatus.IN_PROGRESS,
          start_time: new Date(),
        })
      ).unwrap()

      await dispatch(fetchTimerConfigurationThunk(roomid)).unwrap()

      await dispatch(startNextRoundThunk(roomid)).unwrap()

      await dispatch(getRoomDetails(roomid)).unwrap()

      toast.success('Game started successfully')
    } catch (error) {
      console.error('Failed to start game:', error)
      toast.error('Failed to start game. Please try again.')
    } finally {
      setIsStarting(false)
    }
  }, [
    isNotReady,
    currentRoom?.status,
    isGameCompleted,
    disabled,
    isStarting,
    dispatch,
    roomid,
    scenario_title,
  ])

  const isButtonDisabled = useMemo(
    () =>
      isNotReady ||
      isGameCompleted ||
      disabled ||
      isStarting ||
      currentRoom.status === GameRoomStatus.IN_PROGRESS ||
      currentRoom.status === GameRoomStatus.PAUSED,
    [isNotReady, isGameCompleted, disabled, isStarting, currentRoom.status]
  )

  return (
    <div className='flex flex-col'>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='inline-block'>
              <Button
                className={`flex gap-2 items-center transition-all ${
                  isButtonDisabled
                    ? 'opacity-50 cursor-not-allowed bg-disabled hover:bg-disabled-hovered text-zinc-500'
                    : 'text-white bg-dark-blue hover:bg-dark-blue-hovered'
                } ${className || ''}`}
                onClick={handleStartGame}
                disabled={isButtonDisabled}
              >
                <img
                  src='/icons/rocket.svg'
                  alt='Start Game'
                  className='h-[14.4px] w-[14.4px]'
                />
                <span className='text-base'>
                  {isStarting
                    ? 'Starting Game...'
                    : currentRoom?.status === GameRoomStatus.NOT_STARTED
                      ? 'Start Game'
                      : currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
                          currentRoom?.status === GameRoomStatus.PAUSED
                        ? 'Game in Progress'
                        : 'Game Ended'}
                </span>
              </Button>
            </div>
          </TooltipTrigger>
          {isButtonDisabled && (
            <TooltipContent side='top'>
              {isNotReady && (
                <p>
                  Add at least one player who has joined, add one event, and
                  assign teams to all joined players to start the game.
                  {currentRoom?.game_mode ===
                    TimerMode.SINGLE_ROUND_ADVISOR_ONLY && (
                    <>
                      <br />
                      Assigning at least one advisor to every player is
                      mandatory.
                    </>
                  )}
                  {currentRoom?.game_mode === TimerMode.TEAM_MODE && (
                    <>
                      <br />
                      Selecting team communicators for Round 1 is mandatory for
                      all teams.
                    </>
                  )}
                </p>
              )}
              {currentRoom?.status === GameRoomStatus.IN_PROGRESS && (
                <p>The game is already in progress.</p>
              )}
              {isGameCompleted && (
                <p>This game has ended and cannot be restarted.</p>
              )}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}
